import { setMailboxMessageConfiguration } from 'owa-options-core';
import ReadingPaneOptionState from '../store/schema/ReadingPaneOptionState';
import store from '../store/store';
import { getFallbackValueIfNull } from 'owa-options-core';
import { updateUserConfiguration, UserConfigurationSetting } from 'owa-options-core';
import AfterMoveOrDeleteBehavior from 'owa-service/lib/contract/AfterMoveOrDeleteBehavior2';
import NextSelectionDirection from 'owa-service/lib/contract/NextSelectionDirection';
import UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import { getUserConfiguration } from 'owa-session-store';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import { action, select } from 'satcheljs/lib/legacy';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import { logUsage } from 'owa-analytics';

export interface SaveReadingPaneOptionState {
    optionState?: ReadingPaneOptionState;
    userOptions: UserOptionsType;
}

export type SaveReadingPanePositionOptionStitchName = 'saveReadingPanePositionOptionStitchName';
export type SaveReadingPanePositionOptionStitch = () => void;

const saveReadingPanePositionOptionStitchName: SaveReadingPanePositionOptionStitchName =
    'saveReadingPanePositionOptionStitchName';

let saveReadingPaneOption = action('saveReadingPaneOption')(async function saveReadingPaneOption(
    newReadingPanePosition: ReadingPanePosition,
    userOptions: UserOptionsType,
    newNextSelection?: NextSelectionDirection,
    newLoadFirstMessageOnLogon?: boolean
) {
    let servicePromises: Promise<any>[] = [];
    let serverUpdates: UserConfigurationSetting[] = [];

    let showReadingPane = newReadingPanePosition != ReadingPanePosition.Off;

    // Deal with the new ReadingPane position
    if (newReadingPanePosition !== userOptions.GlobalReadingPanePositionReact) {
        userOptions.GlobalReadingPanePositionReact = newReadingPanePosition;

        // Raise stitch so that, if we're running in mail, it'll trigger 3 line view or single line view
        raiseAction<SaveReadingPanePositionOptionStitch>(saveReadingPanePositionOptionStitchName)();

        serverUpdates.push({
            propertyName: 'GlobalReadingPanePositionReact',
            propertyType: 'Integer32',
            propertyValue: String(newReadingPanePosition),
        });
    }

    // Deal with ShowReadingPaneOnFirstLoad
    if (
        newLoadFirstMessageOnLogon !== undefined &&
        newLoadFirstMessageOnLogon !== userOptions.ShowReadingPaneOnFirstLoad
    ) {
        userOptions.ShowReadingPaneOnFirstLoad = newLoadFirstMessageOnLogon;
        serverUpdates.push({
            propertyName: 'ShowReadingPaneOnFirstLoad',
            propertyType: 'Boolean',
            propertyValue: `${userOptions.ShowReadingPaneOnFirstLoad}`,
        });
    }

    // Send request to update RP position and ShowReadingPaneOnFirstLoad
    if (serverUpdates.length) {
        servicePromises.push(updateUserConfiguration(serverUpdates));
    }

    // Deal with the new 'NextSelection'
    if (showReadingPane && (newNextSelection || userOptions.NextSelection) == 'ReturnToView') {
        newNextSelection = 'Next';
    }

    if (newNextSelection && newNextSelection !== userOptions.NextSelection) {
        userOptions.NextSelection = newNextSelection;

        // This conversion is required because UserOptions deal with string literals (NextSelectionDirection type),
        // whereas updating it on the server deals with numbers (AfterMoveOrDeleteBehavior const enum type)
        let serviceBehavior: AfterMoveOrDeleteBehavior;
        switch (userOptions.NextSelection) {
            case 'Previous':
                serviceBehavior = AfterMoveOrDeleteBehavior.OpenPreviousItem;
                break;
            case 'Next':
                serviceBehavior = AfterMoveOrDeleteBehavior.OpenNextItem;
                break;
            case 'ReturnToView':
                serviceBehavior = AfterMoveOrDeleteBehavior.ReturnToView;
                break;
        }

        servicePromises.push(
            setMailboxMessageConfiguration({
                AfterMoveOrDeleteBehavior: serviceBehavior,
            })
        );
    }

    await Promise.all(servicePromises);
});

export let saveReadingPaneOptionFull = select<SaveReadingPaneOptionState>({
    userOptions: () => getUserConfiguration().UserOptions,
    optionState: () => store,
})(
    action('saveReadingPaneOptionFull')(async function saveReadingPaneOptionFull(
        state?: SaveReadingPaneOptionState
    ) {
        let { userOptions, optionState } = state;

        await saveReadingPaneOption(
            getFallbackValueIfNull(
                optionState.readingPanePosition,
                userOptions.GlobalReadingPanePositionReact
            ),
            userOptions,
            optionState.nextSelection || userOptions.NextSelection,
            getFallbackValueIfNull(
                optionState.loadMessageOnLogOn,
                userOptions.ShowReadingPaneOnFirstLoad
            )
        );
    })
);

const OPTION_QUICK_SETTING_USAGE_KEY = 'QuickSettingReadingPane';

export let saveReadingPaneOptionQuick = select<SaveReadingPaneOptionState>({
    userOptions: () => getUserConfiguration().UserOptions,
})(
    action('saveReadingPaneOptionQuick')(function saveReadingPaneOptionQuick(
        newReadingPanePosition: ReadingPanePosition,
        state?: SaveReadingPaneOptionState
    ) {
        let readingPanePositionString: string;

        switch (newReadingPanePosition) {
            case ReadingPanePosition.Off:
                readingPanePositionString = 'Off';
                break;
            case ReadingPanePosition.Right:
                readingPanePositionString = 'Right';
                break;
            case ReadingPanePosition.Bottom:
                readingPanePositionString = 'Bottom';
                break;
            default:
                readingPanePositionString = 'Unknown';
                break;
        }

        logUsage(OPTION_QUICK_SETTING_USAGE_KEY, [readingPanePositionString]);
        saveReadingPaneOption(newReadingPanePosition, state.userOptions);
    })
);
